import React, { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Container,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Logout,
  AccountBalanceWallet,
  TrendingUp,
  TrendingDown,
  ArrowUpward,
  ArrowDownward,
  Close,
  Receipt,
  Paid
} from '@mui/icons-material';
import { AreaChart, Area, Tooltip, ResponsiveContainer } from 'recharts';

const DashboardPage = () => {
  const { user, logout } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
  
    // Redirect admin users to admin dashboard
    if (user.role === 'admin') {
      navigate('/admin');
      return;
    }
  
    const fetchAccounts = async () => {
      try {
        const { data } = await API.get('/accounts');
        setAccounts(data.accounts);
      } catch (err) {
        console.error('Failed to fetch accounts', err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAccounts();
  }, [user, navigate]);

  // Fetch transactions when accounts are loaded
  useEffect(() => {
    if (accounts.length > 0) {
      const fetchTransactions = async () => {
        try {
          const primaryAccount = accounts[0];
          if (primaryAccount) {
            const { data } = await API.get(`/transactions/${primaryAccount._id}`);
            const recentTransactions = data.history
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 5);
            setTransactions(recentTransactions);
          }
        } catch (err) {
          console.error('Failed to fetch transactions', err);
        } finally {
          setTransactionsLoading(false);
        }
      };

      fetchTransactions();
    }
  }, [accounts]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
  
  const chartData = [
    { name: 'Jan', balance: totalBalance * 0.7 },
    { name: 'Feb', balance: totalBalance * 0.75 },
    { name: 'Mar', balance: totalBalance * 0.82 },
    { name: 'Apr', balance: totalBalance * 0.9 },
    { name: 'May', balance: totalBalance * 0.95 },
    { name: 'Jun', balance: totalBalance },
  ];

  // Get the actual first name from the logged-in user
  const displayName = user?.name || 'User';
  const firstName = displayName.split(' ')[0];
  
  const getUserInitials = () => {
    return displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
    return `$${amount}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Calculate difference in milliseconds
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    // For older dates, show the actual date
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: diffDays > 365 ? 'numeric' : undefined
    });
  };

  // Determine transaction direction based on type and description
  const getTransactionDirection = (transaction) => {
    // For admin transactions, check the description to determine direction
    if (transaction.type === 'admin') {
      if (transaction.description === 'added') {
        return 'inflow'; // This shows as + and green
      }
      if (transaction.description === 'deducted') {
        return 'outflow'; // This shows as - and red
      }
    }
    // For regular transactions, use the existing direction
    return transaction.direction;
  };

  const getTransactionIcon = (type, direction) => {
    if (type === 'admin') return <Paid />;
    if (direction === 'inflow') return <TrendingDown />;
    return <TrendingUp />;
  };

  const getTransactionColor = (type, direction) => {
    if (type === 'admin') return '#60EFFF';
    if (direction === 'inflow') return '#00FF87';
    return '#FF4444';
  };

  const getTransactionTitle = (transaction) => {
    if (transaction.type === 'admin') {
      if (transaction.description === 'added') return 'Deposit';
      if (transaction.description === 'deducted') return 'Withdrawal';
      return 'Balance Adjustment';
    }
    if (transaction.type === 'transfer') return 'Transfer';
    if (transaction.type === 'bill') return 'Bill Payment';
    if (transaction.type === 'external') return 'External Transfer';
    return transaction.direction === 'inflow' ? 'Received' : 'Sent';
  };

  const getTransactionDescription = (transaction) => {
    if (transaction.type === 'admin') {
      if (transaction.description === 'added') {
        return `Funds added to your account`;
      }
      if (transaction.description === 'deducted') {
        return `Funds withdrawn from your account`;
      }
      return 'Account balance updated';
    }
    if (transaction.description) {
      return transaction.description;
    }
    return `${transaction.type} transaction`;
  };

  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#0A0A0A'
      }}>
        <CircularProgress sx={{ color: '#00FF87' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#0A0A0A',
      pb: 8
    }}>
      {/* Header - Removed the menu icon */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0,
          bgcolor: '#0A0A0A',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          px: 2,
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00FF87 0%, #60EFFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            GantryFX
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Logout Button */}
            <Button
              variant="outlined"
              startIcon={<Logout />}
              onClick={handleLogout}
              size="small"
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#FF4444',
                  color: '#FF4444',
                  bgcolor: 'rgba(255,68,68,0.05)'
                }
              }}
            >
              Logout
            </Button>
            
            <Avatar 
              onClick={handleAvatarClick}
              sx={{ 
                bgcolor: '#00FF87', 
                width: 36, 
                height: 36,
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#000',
                cursor: 'pointer'
              }}
            >
              {getUserInitials()}
            </Avatar>
          </Box>
        </Box>
      </Paper>

      {/* Avatar Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: '#111',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: 2,
            minWidth: 150
          }
        }}
      >
        <MenuItem sx={{ color: '#fff' }}>
          <Typography variant="body2">{user?.email}</Typography>
        </MenuItem>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
        <MenuItem onClick={handleLogout} sx={{ color: '#FF4444' }}>
          <Logout sx={{ fontSize: 16, mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ px: 2, py: 3 }}>
        {/* Welcome */}
        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
          Welcome back,
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 3 }}>
          {firstName}
        </Typography>

        {/* Balance Card */}
        <Card sx={{ 
          borderRadius: 4,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 3,
          overflow: 'visible'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              Total Portfolio Value
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', mb: 1 }}>
              {formatCurrency(totalBalance)}
            </Typography>
            {/* Removed the static +12.5% section */}
            
            {/* Chart */}
            <Box sx={{ height: 80, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00FF87" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00FF87" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area 
                    type="monotone" 
                    dataKey="balance" 
                    stroke="#00FF87" 
                    strokeWidth={2}
                    fill="url(#colorBalance)" 
                  />
                  <Tooltip 
                    contentStyle={{ 
                      bgcolor: '#1A1A1A', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff'
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ArrowUpward />}
            onClick={() => navigate('/send')}
            sx={{
              bgcolor: '#00FF87',
              color: '#000',
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#00E077' }
            }}
          >
            Send
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ArrowDownward />}
            onClick={() => navigate('/receive')}
            sx={{
              borderColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#00FF87', bgcolor: 'rgba(0,255,135,0.05)' }
            }}
          >
            Receive
          </Button>
        </Box>

        {/* Assets Section */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
          Your Assets
        </Typography>
        
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 4
        }}>
          <List sx={{ p: 0 }}>
            {accounts.map((account, index) => (
              <React.Fragment key={account._id}>
                <ListItem sx={{ px: 2, py: 2 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'rgba(0,255,135,0.1)', color: '#00FF87' }}>
                      <AccountBalanceWallet />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff', textTransform: 'capitalize' }}>
                        {account.type} Account
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Available Balance
                      </Typography>
                    }
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                    {formatCompactCurrency(account.balance)}
                  </Typography>
                </ListItem>
                {index < accounts.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />}
              </React.Fragment>
            ))}
          </List>
        </Card>

        {/* Recent Activity */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
          Recent Activity
        </Typography>
        
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)'
        }}>
          {transactionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} sx={{ color: '#00FF87' }} />
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Receipt sx={{ fontSize: 48, color: '#666', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                No transactions yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {transactions.map((transaction, index) => {
                const direction = getTransactionDirection(transaction);
                
                return (
                  <React.Fragment key={transaction.id}>
                    <ListItem sx={{ px: 2, py: 2 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: `rgba(${getTransactionColor(transaction.type, direction) === '#00FF87' ? '0,255,135' : getTransactionColor(transaction.type, direction) === '#FF4444' ? '255,68,68' : '96,239,255'}, 0.1)`,
                          color: getTransactionColor(transaction.type, direction)
                        }}>
                          {getTransactionIcon(transaction.type, direction)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>
                            {getTransactionTitle(transaction)}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {getTransactionDescription(transaction)}
                          </Typography>
                        }
                      />
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600, 
                            color: getTransactionColor(transaction.type, direction)
                          }}
                        >
                          {direction === 'inflow' ? '+' : '-'}{formatCurrency(transaction.amount)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {formatDate(transaction.createdAt)}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < transactions.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Card>
      </Container>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default DashboardPage;